import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { API_BASE_URL } from '../../config';
import ApplicationForm from './ApplicationForm';
import './ApplicantPortal.css';

let stripePromise = null;

const getStripePromise = async () => {
    if (!stripePromise) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/stripe/config`);
            const config = await response.json();
            stripePromise = loadStripe(config.publishableKey);
        } catch (error) {
            console.error('Error loading Stripe config:', error);
        }
    }
    return stripePromise;
};

const ApplicantPortal = () => {
    const [applicantData, setApplicantData] = useState(null);
    const [application, setApplication] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Document upload state
    const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [documentUploadFile, setDocumentUploadFile] = useState(null);
    const [documentUploadDescription, setDocumentUploadDescription] = useState('');
    const [documentUploadCategory, setDocumentUploadCategory] = useState('Other');
    
    // Payment state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    
    // Active tab
    const [activeTab, setActiveTab] = useState('overview');
    
    // Lease signing state
    const [leaseSignature, setLeaseSignature] = useState('');
    const [signingLease, setSigningLease] = useState(false);
    const [leaseAgreed, setLeaseAgreed] = useState(false);

    useEffect(() => {
        // Load Stripe
        getStripePromise().then(setStripePromise);
        
        // Check if user is logged in as applicant
        const userType = localStorage.getItem('userType');
        const applicantDataStr = localStorage.getItem('applicantData');
        
        if (userType === 'applicant' && applicantDataStr) {
            const data = JSON.parse(applicantDataStr);
            setApplicantData(data);
            loadApplicationData(data.applicant_id);
        } else {
            // Redirect to login if not authenticated
            window.location.href = '/';
        }
        
        // Check for payment success
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('payment_success') === 'true') {
            setSuccess('Payment processed successfully!');
            setTimeout(() => setSuccess(''), 5000);
            window.history.replaceState({}, document.title, window.location.pathname);
            if (applicantData) {
                loadApplicationData(applicantData.applicant_id);
            }
        }
    }, []);

    const loadApplicationData = async (applicantId) => {
        try {
            setLoading(true);
            
            // Load application details
            const appResponse = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}`);
            if (appResponse.ok) {
                const appData = await appResponse.json();
                setApplication(appData);
            }
            
            // Load application form completion status
            const appFormResponse = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/application`);
            if (appFormResponse.ok) {
                const formData = await appFormResponse.json();
                setApplication(prev => ({
                    ...prev,
                    application_completed: formData.application_completed,
                    application_submitted_at: formData.application_submitted_at
                }));
            }
            
            // Load documents
            const docResponse = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/documents`);
            if (docResponse.ok) {
                const docData = await docResponse.json();
                setDocuments(docData);
            }
            
            // Load payments (application fees, deposits)
            const paymentResponse = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/payments`);
            if (paymentResponse.ok) {
                const paymentData = await paymentResponse.json();
                setPayments(paymentData);
            }
            
        } catch (err) {
            console.error('Error loading application data:', err);
            setError('Failed to load application data');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/';
    };

    const handleDocumentUpload = async (e) => {
        e.preventDefault();
        if (!documentUploadFile) {
            setError('Please select a file to upload');
            return;
        }

        setUploadingDocument(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', documentUploadFile);
            formData.append('description', documentUploadDescription);
            formData.append('category', documentUploadCategory);

            const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantData.applicant_id}/documents`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setSuccess('Document uploaded successfully!');
                setShowDocumentUploadModal(false);
                setDocumentUploadFile(null);
                setDocumentUploadDescription('');
                setDocumentUploadCategory('Other');
                loadApplicationData(applicantData.applicant_id);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to upload document');
            }
        } catch (err) {
            console.error('Error uploading document:', err);
            setError('Failed to upload document');
        } finally {
            setUploadingDocument(false);
        }
    };

    const handleDownloadDocument = async (documentId, filename) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantData.applicant_id}/documents/${documentId}/download`);
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                setError('Failed to download document');
            }
        } catch (err) {
            console.error('Error downloading document:', err);
            setError('Failed to download document');
        }
    };

    const handlePaymentClick = (payment) => {
        setSelectedPayment(payment);
        setShowPaymentModal(true);
    };

    const handleStripeCheckout = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    payment_id: selectedPayment.id,
                    payment_type: 'both',
                    success_url: `${window.location.origin}/applicant-portal?payment_success=true`,
                    cancel_url: `${window.location.origin}/applicant-portal?payment_cancelled=true`
                })
            });
            
            const data = await response.json();
            if (response.ok && data.url) {
                window.location.href = data.url;
            } else {
                throw new Error(data.error || 'Failed to create checkout session');
            }
        } catch (err) {
            console.error('Error creating checkout session:', err);
            setError(err.message || 'Failed to start payment. Please try again.');
            setLoading(false);
        }
    };

    const handleSignLease = async () => {
        if (!leaseSignature.trim()) {
            setError('Please type your full legal name as your electronic signature');
            return;
        }
        if (!leaseAgreed) {
            setError('Please agree to the lease terms');
            return;
        }
        
        try {
            setSigningLease(true);
            const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantData.applicant_id}/sign-lease`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    signature: leaseSignature,
                    signed_at: new Date().toISOString()
                })
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to sign lease');
            
            setSuccess('Lease signed successfully! Welcome to your new home!');
            loadApplicationData(applicantData.applicant_id);
            setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
            console.error('Error signing lease:', err);
            setError(err.message || 'Failed to sign lease. Please try again.');
        } finally {
            setSigningLease(false);
        }
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'new': '#3b82f6',
            'contacted': '#8b5cf6',
            'application': '#f59e0b',
            'review': '#f59e0b',
            'approved': '#10b981',
            'rejected': '#ef4444',
            'lease_signed': '#10b981'
        };
        return statusColors[status] || '#6b7280';
    };

    if (loading) {
        return (
            <div className="applicant-portal">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading your application...</p>
                </div>
            </div>
        );
    }

    if (!applicantData) {
        return (
            <div className="applicant-portal">
                <div className="error-container">
                    <p>Not authenticated. Redirecting to login...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="applicant-portal">
            {/* Header */}
            <header className="applicant-header">
                <div className="header-content">
                    <h1>Application Portal</h1>
                    <div className="header-actions">
                        <span className="applicant-name">
                            {applicantData.first_name} {applicantData.last_name}
                        </span>
                        <button className="logout-btn" onClick={handleLogout}>Logout</button>
                    </div>
                </div>
            </header>

            {/* Status Messages */}
            {error && (
                <div className="alert alert-error">
                    {error}
                    <button onClick={() => setError('')}>×</button>
                </div>
            )}
            {success && (
                <div className="alert alert-success">
                    {success}
                    <button onClick={() => setSuccess('')}>×</button>
                </div>
            )}

            {/* Main Content */}
            <div className="applicant-content">
                {/* Sidebar Navigation */}
                <aside className="applicant-sidebar">
                    <nav className="sidebar-nav">
                        <button 
                            className={activeTab === 'overview' ? 'active' : ''}
                            onClick={() => setActiveTab('overview')}
                        >
                            📋 Overview
                        </button>
                        <button 
                            className={activeTab === 'application' ? 'active' : ''}
                            onClick={() => setActiveTab('application')}
                        >
                            📝 Application
                        </button>
                        <button 
                            className={activeTab === 'documents' ? 'active' : ''}
                            onClick={() => setActiveTab('documents')}
                        >
                            📄 Documents
                        </button>
                        <button 
                            className={activeTab === 'payments' ? 'active' : ''}
                            onClick={() => setActiveTab('payments')}
                        >
                            💳 Payments
                        </button>
                        {/* Show Lease tab only when approved */}
                        {application && ['approved', 'lease_signed'].includes(application.status) && (
                            <button 
                                className={activeTab === 'lease' ? 'active' : ''}
                                onClick={() => setActiveTab('lease')}
                            >
                                📋 Lease
                            </button>
                        )}
                    </nav>
                </aside>

                {/* Main Panel */}
                <main className="applicant-main">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="tab-content">
                            <h2>Application Overview</h2>
                            
                            {application && (
                                <div className="status-card">
                                    <div className="status-header">
                                        <h3>Application Status</h3>
                                        <span 
                                            className="status-badge"
                                            style={{ backgroundColor: getStatusColor(application.status) }}
                                        >
                                            {application.status?.replace('_', ' ').toUpperCase() || 'NEW'}
                                        </span>
                                    </div>
                                    
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Property Interest</label>
                                            <p>{application.property_interest || 'Not specified'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Move-In Date</label>
                                            <p>{application.move_in_date || 'Not specified'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Lease Length</label>
                                            <p>{application.lease_length || 'Not specified'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Assigned Agent</label>
                                            <p>{application.assigned_agent || 'Not assigned'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="quick-stats">
                                <div className="stat-card">
                                    <div className="stat-value">{documents.length}</div>
                                    <div className="stat-label">Documents</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-value">
                                        ${payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                                    </div>
                                    <div className="stat-label">Total Paid</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-value">
                                        ${payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                                    </div>
                                    <div className="stat-label">Pending</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Application Tab */}
                    {activeTab === 'application' && (
                        <div className="tab-content">
                            <h2>Rental Application</h2>
                            
                            {application && application.application_completed ? (
                                <div className="application-completed">
                                    <div className="completion-badge">
                                        <span className="badge-icon">✓</span>
                                        <span>Application Submitted</span>
                                    </div>
                                    <p className="completion-date">
                                        Submitted on: {application.application_submitted_at 
                                            ? new Date(application.application_submitted_at).toLocaleDateString()
                                            : 'N/A'}
                                    </p>
                                    <div className="application-details">
                                        <div className="detail-section">
                                            <h3>Personal Information</h3>
                                            <div className="detail-grid">
                                                <div><label>Name:</label> {application.first_name} {application.last_name}</div>
                                                <div><label>Email:</label> {application.email}</div>
                                                <div><label>Phone:</label> {application.phone || 'Not provided'}</div>
                                            </div>
                                        </div>

                                        {application.employer_name && (
                                            <div className="detail-section">
                                                <h3>Employment</h3>
                                                <div className="detail-grid">
                                                    <div><label>Employer:</label> {application.employer_name}</div>
                                                    <div><label>Job Title:</label> {application.job_title || 'Not provided'}</div>
                                                    <div><label>Status:</label> {application.employment_status || 'Not provided'}</div>
                                                </div>
                                            </div>
                                        )}

                                        {application.monthly_income && (
                                            <div className="detail-section">
                                                <h3>Financial Information</h3>
                                                <div className="detail-grid">
                                                    <div><label>Monthly Income:</label> ${application.monthly_income?.toLocaleString()}</div>
                                                    {application.credit_score && <div><label>Credit Score:</label> {application.credit_score}</div>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="application-form-container">
                                    <div className="application-intro">
                                        <h3>Complete Your Rental Application</h3>
                                        <p>Please fill out all sections of the application form below. You can save your progress and return later.</p>
                                    </div>
                                    <ApplicationForm 
                                        applicantId={applicantData.applicant_id}
                                        onComplete={() => {
                                            loadApplicationData(applicantData.applicant_id);
                                            setSuccess('Application submitted successfully!');
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>Documents</h2>
                                <button 
                                    className="upload-btn"
                                    onClick={() => setShowDocumentUploadModal(true)}
                                >
                                    + Upload Document
                                </button>
                            </div>

                            {documents.length === 0 ? (
                                <div className="empty-state">
                                    <p>No documents uploaded yet.</p>
                                </div>
                            ) : (
                                <div className="documents-list">
                                    {documents.map(doc => (
                                        <div key={doc.id} className="document-item">
                                            <div className="document-info">
                                                <h4>{doc.original_filename}</h4>
                                                <p className="document-meta">
                                                    {doc.category} • {new Date(doc.upload_date).toLocaleDateString()}
                                                </p>
                                                {doc.description && <p className="document-description">{doc.description}</p>}
                                            </div>
                                            <button 
                                                className="download-btn"
                                                onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                                            >
                                                Download
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Payments Tab */}
                    {activeTab === 'payments' && (
                        <div className="tab-content">
                            <h2>Payments</h2>
                            
                            {payments.length === 0 ? (
                                <div className="empty-state">
                                    <p>No payments required at this time.</p>
                                </div>
                            ) : (
                                <div className="payments-list">
                                    {payments.map(payment => (
                                        <div key={payment.id} className="payment-item">
                                            <div className="payment-info">
                                                <h4>{payment.description || payment.payment_type || 'Payment'}</h4>
                                                <p className="payment-amount">${payment.amount?.toFixed(2)}</p>
                                                <p className="payment-meta">
                                                    Due: {new Date(payment.due_date).toLocaleDateString()}
                                                    {payment.paid_date && ` • Paid: ${new Date(payment.paid_date).toLocaleDateString()}`}
                                                </p>
                                            </div>
                                            <div className="payment-status">
                                                <span className={`status-badge status-${payment.status}`}>
                                                    {payment.status?.toUpperCase()}
                                                </span>
                                                {payment.status === 'pending' && (
                                                    <button 
                                                        className="pay-btn"
                                                        onClick={() => handlePaymentClick(payment)}
                                                    >
                                                        Pay Now
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Lease Tab */}
                    {activeTab === 'lease' && application && (
                        <div className="tab-content">
                            <h2>Lease Agreement</h2>
                            
                            {application.status === 'lease_signed' || application.lease_signed ? (
                                <div className="lease-signed-container">
                                    <div className="lease-signed-badge">
                                        <span className="badge-icon">✓</span>
                                        <span>Lease Signed</span>
                                    </div>
                                    <p className="lease-signed-date">
                                        Signed on: {application.lease_signed_at 
                                            ? new Date(application.lease_signed_at).toLocaleDateString()
                                            : 'Date recorded'}
                                    </p>
                                    <div className="lease-summary">
                                        <h3>Your Lease Details</h3>
                                        <div className="lease-details-grid">
                                            <div className="lease-detail">
                                                <label>Property</label>
                                                <p>{application.property_interest || 'See your welcome email'}</p>
                                            </div>
                                            <div className="lease-detail">
                                                <label>Monthly Rent</label>
                                                <p>${application.approved_rent?.toLocaleString() || 'TBD'}</p>
                                            </div>
                                            <div className="lease-detail">
                                                <label>Move-in Date</label>
                                                <p>{application.move_in_date || 'TBD'}</p>
                                            </div>
                                            <div className="lease-detail">
                                                <label>Lease Term</label>
                                                <p>{application.lease_length || '12 months'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="next-steps">
                                        <h4>Next Steps</h4>
                                        <p>Your property manager will be in touch with move-in details. 
                                        Once you move in, your account will be upgraded to a full tenant portal 
                                        where you can pay rent, submit maintenance requests, and more.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="lease-signing-container">
                                    <div className="congratulations-banner">
                                        <h3>🎉 Congratulations!</h3>
                                        <p>Your application has been approved. Please review and sign your lease agreement below.</p>
                                    </div>

                                    <div className="lease-preview">
                                        <h3>Lease Summary</h3>
                                        <div className="lease-details-grid">
                                            <div className="lease-detail">
                                                <label>Property</label>
                                                <p>{application.property_interest || 'See details below'}</p>
                                            </div>
                                            <div className="lease-detail">
                                                <label>Monthly Rent</label>
                                                <p>${application.approved_rent?.toLocaleString() || application.budget_max?.toLocaleString() || 'TBD'}</p>
                                            </div>
                                            <div className="lease-detail">
                                                <label>Move-in Date</label>
                                                <p>{application.move_in_date || 'To be confirmed'}</p>
                                            </div>
                                            <div className="lease-detail">
                                                <label>Lease Term</label>
                                                <p>{application.lease_length || '12 months'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="lease-terms-box">
                                        <h4>Lease Terms & Conditions</h4>
                                        <div className="terms-content">
                                            <p>By signing below, you agree to the following:</p>
                                            <ul>
                                                <li>Pay monthly rent on or before the 1st of each month</li>
                                                <li>Maintain the property in good condition</li>
                                                <li>Follow all community rules and regulations</li>
                                                <li>Provide 30 days notice before vacating</li>
                                                <li>Allow property inspections with 24-hour notice</li>
                                                <li>Not sublease without written permission</li>
                                            </ul>
                                            <p><em>The full lease document will be provided by your property manager.</em></p>
                                        </div>
                                    </div>

                                    <div className="signature-section">
                                        <h4>Electronic Signature</h4>
                                        <div className="signature-agreement">
                                            <label className="checkbox-label">
                                                <input 
                                                    type="checkbox" 
                                                    checked={leaseAgreed}
                                                    onChange={(e) => setLeaseAgreed(e.target.checked)}
                                                />
                                                <span>I have read and agree to the lease terms and conditions</span>
                                            </label>
                                        </div>
                                        <div className="signature-input-group">
                                            <label>Type your full legal name as your electronic signature:</label>
                                            <input 
                                                type="text"
                                                className="signature-input"
                                                value={leaseSignature}
                                                onChange={(e) => setLeaseSignature(e.target.value)}
                                                placeholder={`${applicantData.first_name} ${applicantData.last_name}`}
                                            />
                                        </div>
                                        <button 
                                            className="sign-lease-btn"
                                            onClick={handleSignLease}
                                            disabled={signingLease || !leaseAgreed || !leaseSignature.trim()}
                                        >
                                            {signingLease ? 'Signing...' : '✍️ Sign Lease Agreement'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Document Upload Modal */}
            {showDocumentUploadModal && (
                <div className="modal-overlay" onClick={() => setShowDocumentUploadModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Upload Document</h3>
                            <button className="close-btn" onClick={() => setShowDocumentUploadModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleDocumentUpload}>
                            <div className="form-group">
                                <label>File</label>
                                <input 
                                    type="file" 
                                    onChange={(e) => setDocumentUploadFile(e.target.files[0])}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select 
                                    value={documentUploadCategory}
                                    onChange={(e) => setDocumentUploadCategory(e.target.value)}
                                >
                                    <option value="ID">ID/Identification</option>
                                    <option value="Income">Income Verification</option>
                                    <option value="Reference">Reference Letter</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea 
                                    value={documentUploadDescription}
                                    onChange={(e) => setDocumentUploadDescription(e.target.value)}
                                    placeholder="Optional description"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowDocumentUploadModal(false)}>Cancel</button>
                                <button type="submit" disabled={uploadingDocument}>
                                    {uploadingDocument ? 'Uploading...' : 'Upload'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && selectedPayment && (
                <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Make Payment</h3>
                            <button className="close-btn" onClick={() => setShowPaymentModal(false)}>×</button>
                        </div>
                        <div className="payment-modal-content">
                            <p><strong>Amount:</strong> ${selectedPayment.amount?.toFixed(2)}</p>
                            <p><strong>Description:</strong> {selectedPayment.description || selectedPayment.payment_type}</p>
                            <button 
                                className="stripe-checkout-btn"
                                onClick={handleStripeCheckout}
                            >
                                Pay with Stripe
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicantPortal;
